import { FormattedMessage, useIntl } from "react-intl";
import { TextField, useMediaQuery } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import type { JSX } from "react";
import { fieldVariant } from "../../../utils/AppUtils";

// Declared in manualAddressAdapters so non-React consumers can use the shape; re-exported here
// because this module is the established import site for them.
import type { IManualAddressFormValues } from "./manualAddressAdapters";

export { defaultManualAddressValues } from "./manualAddressAdapters";
export type { IManualAddressFormValues } from "./manualAddressAdapters";

interface IProps {
    control: Control<IManualAddressFormValues>;
}

const ManualAddressFields = ({ control }: IProps): JSX.Element => {
    const { formatMessage } = useIntl();
    const smallerScreen = useMediaQuery("(max-width:899px)");
    const size = smallerScreen ? "small" : undefined;

    return (
        <>
            <Controller
                name="addressLine1"
                control={control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                    <TextField
                        {...field}
                        variant={fieldVariant}
                        size={size}
                        margin="normal"
                        color="primary"
                        required
                        fullWidth
                        autoComplete="address-line1"
                        label={<FormattedMessage id="common.address.label.line-1" defaultMessage="Address Line 1" />}
                        placeholder={formatMessage({ id: "manual-address.placeholder.address-line1", defaultMessage: "Street address" })}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        error={invalid}
                        helperText={invalid ? <FormattedMessage id="manual-address.error.required" defaultMessage="This field is required" /> : " "}
                    />
                )}
            />
            <Controller
                name="addressLine2"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                    <TextField
                        {...field}
                        variant={fieldVariant}
                        size={size}
                        margin="normal"
                        color="primary"
                        fullWidth
                        autoComplete="address-line2"
                        label={<FormattedMessage id="common.address.label.line-2-optional" defaultMessage="Address Line 2 (optional)" />}
                        placeholder={formatMessage({ id: "common.address.placeholder.line-2", defaultMessage: "Enter your address" })}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        helperText=" "
                    />
                )}
            />
            <Controller
                name="city"
                control={control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                    <TextField
                        {...field}
                        variant={fieldVariant}
                        size={size}
                        margin="normal"
                        color="primary"
                        required
                        fullWidth
                        autoComplete="address-level2"
                        label={<FormattedMessage id="common.address.label.city" defaultMessage="City" />}
                        placeholder={formatMessage({ id: "common.address.label.city", defaultMessage: "City" })}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        error={invalid}
                        helperText={invalid ? <FormattedMessage id="manual-address.error.required" defaultMessage="This field is required" /> : " "}
                    />
                )}
            />
            <Controller
                name="county"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                    <TextField
                        {...field}
                        variant={fieldVariant}
                        size={size}
                        margin="normal"
                        color="primary"
                        fullWidth
                        label={<FormattedMessage id="common.address.label.county" defaultMessage="County" />}
                        placeholder={formatMessage({ id: "common.address.label.county", defaultMessage: "County" })}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        helperText=" "
                    />
                )}
            />
            <Controller
                name="state"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                    <TextField
                        {...field}
                        variant={fieldVariant}
                        size={size}
                        margin="normal"
                        color="primary"
                        fullWidth
                        autoComplete="address-level1"
                        label={<FormattedMessage id="common.address.label.state-province" defaultMessage="State/Province" />}
                        placeholder={formatMessage({ id: "common.address.label.state-province", defaultMessage: "State/Province" })}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        helperText=" "
                    />
                )}
            />
            <Controller
                name="zipCode"
                control={control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                    <TextField
                        {...field}
                        variant={fieldVariant}
                        size={size}
                        margin="normal"
                        color="primary"
                        required
                        fullWidth
                        autoComplete="postal-code"
                        label={<FormattedMessage id="common.address.label.zip-code" defaultMessage="Zip Code" />}
                        placeholder={formatMessage({ id: "profile.contact-points.placeholder.address-zip-code", defaultMessage: "Zip code" })}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        error={invalid}
                        helperText={invalid ? <FormattedMessage id="manual-address.error.required" defaultMessage="This field is required" /> : " "}
                    />
                )}
            />
        </>
    );
};

export default ManualAddressFields;
